import { Fragment } from "react";
import { useTranslation } from "@1f/react-sdk";
import VaporUploader from "@vapor/react-custom/VaporUploader";
import Divider from "@vapor/react-material/Divider";
import Stack from "@vapor/react-material/Stack";
import IconButton from "@vapor/react-material/IconButton";
import Table from "@vapor/react-material/Table";
import TableBody from "@vapor/react-material/TableBody";
import TableCell from "@vapor/react-material/TableCell";
import TableContainer from "@vapor/react-material/TableContainer";
import TableHead from "@vapor/react-material/TableHead";
import TableRow from "@vapor/react-material/TableRow";
import Paper from "@vapor/react-material/Paper";
import Button from "@vapor/react-material/Button";
import TextField from "@vapor/react-material/TextField";
import Autocomplete from "@vapor/react-material/Autocomplete";
import ExtendedTypography from "@vapor/react-extended/ExtendedTypography";
import { AdapterDateFns } from "@vapor/react-x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@vapor/react-x-date-pickers/LocalizationProvider";
import { DatePicker } from "@vapor/react-x-date-pickers/DatePicker";
import { it } from "date-fns/locale";
import {
	InsertDriveFile as FileIcon,
	Delete as DeleteIcon,
	ArrowForward as DetailIcon,
} from "@mui/icons-material";
import { Loading } from "../Loading";
import { NoData } from "../NoData";
import { isDateValid } from "../../businessLogic/helpers/dateHelpers";
import { v4 as uuidv4 } from "uuid";

export const SosTab = ({
	backDropInternalOpen,
	currentSOS,
	sosAllegati,
	currentSOSList,
	pendingSosFiles,
	utentiList,
	userPermissions,
	userPermissionsSOS,
	allBrowserFiles,
	handleChangeDateSOS,
	handleChangeSOS,
	handleChangeDateRicUifSOS,
	handleCancelSOS,
	handleBtnAddSOSClick,
	handleDeleteSOSClick,
	handleSelectedSOSClick,
	handleUploadFile,
	handleDownloadAllegatoClick,
	handleDeleteAllegatoClick,
}) => {
	const { t } = useTranslation();
	const canSaveSOS = currentSOS.id > 0 ? userPermissionsSOS.canUpdate : userPermissionsSOS.canCreate;

	return (
		<Fragment>
			<Loading open={backDropInternalOpen} />

			<ExtendedTypography variant="titleSmall" color="primary.main">
				{currentSOS.id === 0
					? t("fascicolo.fascicoloAdd.createNew")
					: "Modifica la segnalazione SOS"}
			</ExtendedTypography>
			<Divider variant="standard" sx={{ mt: 1, mb: 1 }} />

			<Stack direction="column" spacing={1}>
				<Stack direction="row" spacing={2}>
					<LocalizationProvider
						dateAdapter={AdapterDateFns}
						adapterLocale={it}
					>
						<DatePicker
							label="Data"
							closeOnSelect
							format="dd/MM/yyyy"
							mask="__/__/____"
							maxDate={new Date()}
							value={
								isDateValid(currentSOS.date)
									? new Date(currentSOS.date)
									: null
							}
							onChange={handleChangeDateSOS}
							slotProps={{
								textField: {
									required: true,
									placeholder: "dd/MM/yyyy",
									size: "small",
									fullWidth: true,
								},
							}}
						/>
					</LocalizationProvider>

					<TextField
						id="txtReceiptId"
						key={uuidv4()}
						label="Prot. N."
						defaultValue={currentSOS.receiptId}
						size="small"
						fullWidth
						required
						type="tel"
						onChange={handleChangeSOS}
						inputProps={{ maxLength: 20 }}
					/>

					<Autocomplete
						id="ddlReportedBy"
						key={uuidv4()}
						size="small"
						options={utentiList}
						sx={{ width: "100%" }}
						fullWidth
						onChange={handleChangeSOS}
						isOptionEqualToValue={(option, value) =>
							currentSOS.reportedByUser === undefined
								? option.userId === value.userId
								: option.userId === currentSOS.reportedByUser
						}
						getOptionLabel={(option) => option.completeName}
						defaultValue={
							currentSOS.reportedByUser !== undefined
								? utentiList.filter(
										(item) => item.userId === currentSOS.reportedByUser,
									)[0]
								: null
						}
						renderInput={(params) => (
							<TextField
								{...params}
								required
								label="Segnalata da"
								sx={{ lineHeight: 1 }}
							/>
						)}
					/>
				</Stack>
				<Stack direction="column" spacing={1}>
					<LocalizationProvider
						dateAdapter={AdapterDateFns}
						adapterLocale={it}
					>
						<DatePicker
							label="Data ricezione UIF"
							closeOnSelect
							format="dd/MM/yyyy"
							mask="__/__/____"
							maxDate={new Date()}
							value={
								isDateValid(currentSOS.dateRicUif)
									? new Date(currentSOS.dateRicUif)
									: null
							}
							onChange={handleChangeDateRicUifSOS}
							slotProps={{
								textField: {
									required: true,
									placeholder: "dd/MM/yyyy",
									size: "small",
									fullWidth: true,
								},
							}}
						/>
					</LocalizationProvider>
					<TextField
						id="txtMotivo"
						key={uuidv4()}
						label="Motivi del sospetto accertati/presunti"
						defaultValue={currentSOS.motivo}
						required
						size="small"
						fullWidth
						multiline
						rows={6}
						onChange={handleChangeSOS}
						inputProps={{ autocomplete: "nope" }}
					/>
					<TextField
						id="txtNote"
						key={uuidv4()}
						label="Note"
						defaultValue={currentSOS.note}
						size="small"
						fullWidth
						multiline
						rows={2}
						onChange={handleChangeSOS}
						inputProps={{ autocomplete: "nope" }}
					/>
				</Stack>

				<Stack>
					{userPermissions.canCreate && canSaveSOS && <VaporUploader
						buttonVariant="contained"
						draggable
						multiple
						label="Aggiungi file alla SOS"
						draggableText="Trascina qui i tuoi files PDF (MAX 10MB)"
						dropText="Rilascia qui i tuoi files PDF (MAX 10MB)"
						buttonLabel="Seleziona"
						onFileUpload={handleUploadFile("sos", currentSOS.id)}
						files={
							currentSOS.id > 0 ? allBrowserFiles : pendingSosFiles
						}
						inputFileProps={{
							multiple: true,
						}}
					/>}
					{currentSOS.id <= 0 && (
						<ExtendedTypography variant="caption" color="warning.main">
							Puoi allegare file anche prima del salvataggio: verranno
							associati alla SOS quando premi Salva.
						</ExtendedTypography>
					)}

					{sosAllegati.length > 0 ? (
						<TableContainer>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell align="left" width="90%">
											<ExtendedTypography variant="subtitle2">
												Documenti allegati alla SOS
											</ExtendedTypography>
										</TableCell>
										<TableCell align="center" width="10%"></TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{sosAllegati.map((item) => (
										<TableRow key={uuidv4()} hover>
											<TableCell
												align="left"
												width="90%"
												onClick={() =>
													handleDownloadAllegatoClick(item)
												}
												sx={{
													cursor:
														item.id > 233 && item.id <= 622
															? "default"
															: "pointer",
												}}
											>
												<Stack
													direction="row"
													spacing={1}
													justifyContent="left"
													alignItems="center"
												>
													<FileIcon />
													<Stack direction="column" spacing={0}>
														<ExtendedTypography variant="subtitle2">
															{item.displayedName}
															{item.id > 233 && item.id <= 622 ? (
																<span
																	style={{
																		color: "red",
																		fontSize: "0.75rem",
																	}}
																>
																	*Documento non disponibile
																</span>
															) : (
																""
															)}
														</ExtendedTypography>
														<ExtendedTypography variant="caption">
															{new Date(
																item.createdOn,
															).toLocaleDateString()}{" "}
															File: {item.fileName}
														</ExtendedTypography>
													</Stack>
												</Stack>
											</TableCell>
											<TableCell align="center" width="10%">
												{userPermissions.canDelete ? (
													<IconButton
														edge="end"
														aria-label="delete"
														color="error"
														onClick={() =>
															handleDeleteAllegatoClick(item)
														}
													>
														<DeleteIcon />
													</IconButton>
												) : null}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					) : (
						<NoData size="subtitle" sx={{ mt: 4 }} />
					)}
				</Stack>

				<Stack
					direction="row"
					spacing={1}
					justifyContent="right"
					alignItems="right"
				>
					<Button
						variant="outlined"
						size="small"
						onClick={handleCancelSOS}
					>
						{t("actions.annulla")}
					</Button>
					{canSaveSOS ? (
						<Button
							variant="contained"
							size="small"
							onClick={handleBtnAddSOSClick}
						>
							{t("actions.salva")}
						</Button>
					) : null}
				</Stack>
			</Stack>

			<ExtendedTypography variant="titleSmall" color="primary.main">
				{t("fascicolo.fascicoloAdd.elenco")}
			</ExtendedTypography>
			<Divider variant="standard" sx={{ mt: 1, mb: 1 }} />

			{currentSOSList.length > 0 ? (
				<Fragment>
					<TableContainer
						component={Paper}
						sx={{ width: "100%", mt: 4 }}
					>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell align="left" width="25%">
										<ExtendedTypography variant="subtitle2">
											{t("fascicolo.fascicoloAdd.dati")}
										</ExtendedTypography>
									</TableCell>
									<TableCell align="left" width="25%">
										<ExtendedTypography variant="subtitle2">
											{t("fascicolo.fascicoloAdd.protNr")}
										</ExtendedTypography>
									</TableCell>
									<TableCell align="left" width="30%">
										<ExtendedTypography variant="subtitle2">
											Effettuata da
										</ExtendedTypography>
									</TableCell>
									<TableCell align="center" width="20%"></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{currentSOSList.map((item) => (
									<TableRow
										key={uuidv4()}
										hover
										onClick={() => handleSelectedSOSClick(item)}
										sx={{ cursor: "pointer" }}
									>
										<TableCell align="left" width="25%">
											<ExtendedTypography variant="subtitle2">
												{new Date(item.date).toLocaleDateString()}
											</ExtendedTypography>
										</TableCell>
										<TableCell align="left" width="25%">
											<ExtendedTypography variant="subtitle2">
												{item.receiptId}
											</ExtendedTypography>
										</TableCell>
										<TableCell align="left" width="25%">
											<ExtendedTypography variant="subtitle2">
												{utentiList.filter(
													(x) => x.userId === item.reportedByUser,
												)[0].completeName + " "}
											</ExtendedTypography>
										</TableCell>
										<TableCell align="center" width="20%">
											{userPermissionsSOS.canDelete ? (
												<IconButton
													onClick={() => handleDeleteSOSClick(item)}
													color="error"
												>
													<DeleteIcon />
												</IconButton>
											) : null}
											<IconButton
												onClick={() => handleSelectedSOSClick(item)}
												color="primary"
											>
												<DetailIcon />
											</IconButton>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</Fragment>
			) : (
				<NoData size="small" />
			)}
		</Fragment>
	);
};
